/* eslint-disable */
import { Chore, AssignedChore } from '../types';
import { chores } from '../data/chores';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, parseISO } from 'date-fns';

export function assignChoresForWeek(startDate: Date): AssignedChore[] {
  const weekStart = startOfWeek(startDate, { weekStartsOn: 1 }); // Monday
  const weekEnd = endOfWeek(startDate, { weekStartsOn: 1 }); // Sunday
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });
  
  const assignedChores: AssignedChore[] = [];
  
  // Get chores that need to be done this week
  const weeklyChores = getChoresForWeek(startDate);
  
  // Group chores by frequency
  const weeklyChoresList = weeklyChores.filter(chore => chore.frequency === 'Weekly');
  const twiceWeeklyChores = weeklyChores.filter(chore => chore.frequency === '2x Weekly');
  const thriceWeeklyChores = weeklyChores.filter(chore => chore.frequency === '3x Weekly');
  const biweeklyChores = weeklyChores.filter(chore => chore.frequency === 'Every 2 Weeks');
  const monthlyChores = weeklyChores.filter(chore => chore.frequency === 'Monthly');
  
  // Initialize daily time tracking
  const dailyTime: { [key: string]: { mary: number; maxwell: number } } = {};
  weekDays.forEach(day => {
    dailyTime[format(day, 'yyyy-MM-dd')] = { mary: 0, maxwell: 0 };
  });
  
  // Initialize weekly time tracking
  let maryWeeklyTime = 0;
  let maxwellWeeklyTime = 0;
  
  // Helper function to assign chore
  const assignChore = (chore: Chore, day: Date, assignedTo: 'Mary' | 'Maxwell') => {
    const dayKey = format(day, 'yyyy-MM-dd');
    const dayData = dailyTime[dayKey];
    
    const assignedChore: AssignedChore = {
      id: `${chore.id}-${dayKey}`,
      choreId: chore.id,
      task: chore.task,
      assignedTo,
      date: dayKey,
      completed: false,
      timeMinutes: chore.timeMinutes
    };
    
    assignedChores.push(assignedChore);
    
    // Update time tracking
    if (assignedTo === 'Mary') {
      dayData.mary += chore.timeMinutes;
      maryWeeklyTime += chore.timeMinutes;
    } else {
      dayData.maxwell += chore.timeMinutes;
      maxwellWeeklyTime += chore.timeMinutes;
    }
  };
  
  // Step 1: Collect all chores that need to be assigned this week
  const allChoresToAssign: { chore: Chore; day: Date }[] = [];
  
  // Add weekly chores
  weeklyChoresList.forEach((chore, index) => {
    const dayIndex = index % 7;
    allChoresToAssign.push({ chore, day: weekDays[dayIndex] });
  });
  
  // Add other frequency chores
  weekDays.forEach(day => {
    const dayChores = getChoresForDay(day, [...twiceWeeklyChores, ...thriceWeeklyChores, ...biweeklyChores, ...monthlyChores]);
    dayChores.forEach(chore => {
      allChoresToAssign.push({ chore, day });
    });
  });
  
  // Step 2: Sort chores by time (longer chores first for better distribution)
  allChoresToAssign.sort((a, b) => b.chore.timeMinutes - a.chore.timeMinutes);
  
  // Step 3: Assign chores with smart balancing
  allChoresToAssign.forEach(({ chore, day }) => {
    const dayKey = format(day, 'yyyy-MM-dd');
    const dayData = dailyTime[dayKey];
    
    // Determine who should do this chore
    let assignedTo: 'Mary' | 'Maxwell';
    
    // First priority: balance weekly totals
    if (Math.abs(maryWeeklyTime - maxwellWeeklyTime) > 10) {
      assignedTo = maryWeeklyTime <= maxwellWeeklyTime ? 'Mary' : 'Maxwell';
    } else {
      // Second priority: balance daily totals
      const maryTimeToday = dayData.mary;
      const maxwellTimeToday = dayData.maxwell;
      
      if (Math.abs(maryTimeToday - maxwellTimeToday) > 10) {
        assignedTo = maryTimeToday <= maxwellTimeToday ? 'Mary' : 'Maxwell';
      } else {
        // Third priority: ensure each person has at least one chore per day
        const maryChoresToday = assignedChores.filter(c => 
          c.date === dayKey && c.assignedTo === 'Mary'
        );
        const maxwellChoresToday = assignedChores.filter(c => 
          c.date === dayKey && c.assignedTo === 'Maxwell'
        );
        
        if (maryChoresToday.length === 0 && maxwellChoresToday.length > 0) {
          assignedTo = 'Mary';
        } else if (maxwellChoresToday.length === 0 && maryChoresToday.length > 0) {
          assignedTo = 'Maxwell';
        } else {
          // Default: balance weekly totals
          assignedTo = maryWeeklyTime <= maxwellWeeklyTime ? 'Mary' : 'Maxwell';
        }
      }
    }
    
    assignChore(chore, day, assignedTo);
  });
  
  // Step 4: Post-assignment optimization
  // Try to move chores to better balance daily and weekly totals
  let optimizationRounds = 0;
  const maxOptimizationRounds = 3;
  
  /* eslint-disable no-loop-func */
  while (optimizationRounds < maxOptimizationRounds) {
    let madeChanges = false;
    
    weekDays.forEach(day => {
      const dayKey = format(day, 'yyyy-MM-dd');
      const dayData = dailyTime[dayKey];
      const dayChores = assignedChores.filter(c => c.date === dayKey);
      
      // Find the best chore to move for better balance
      const maryChoresToday = dayChores.filter(c => c.assignedTo === 'Mary');
      const maxwellChoresToday = dayChores.filter(c => c.assignedTo === 'Maxwell');
      
      // If weekly totals are significantly unbalanced, try to move a chore
      if (Math.abs(maryWeeklyTime - maxwellWeeklyTime) > 15) {
        if (maryWeeklyTime > maxwellWeeklyTime && maryChoresToday.length > 0) {
          // Move a chore from Mary to Maxwell
          const choreToMove = maryChoresToday.find(c => {
            const timeDiff = maryWeeklyTime - maxwellWeeklyTime;
            return c.timeMinutes <= timeDiff && dayData.mary - c.timeMinutes >= 0;
          });
          
          if (choreToMove) {
            choreToMove.assignedTo = 'Maxwell';
            dayData.mary -= choreToMove.timeMinutes;
            dayData.maxwell += choreToMove.timeMinutes;
            maryWeeklyTime -= choreToMove.timeMinutes;
            maxwellWeeklyTime += choreToMove.timeMinutes;
            madeChanges = true;
          }
        } else if (maxwellWeeklyTime > maryWeeklyTime && maxwellChoresToday.length > 0) {
          // Move a chore from Maxwell to Mary
          const choreToMove = maxwellChoresToday.find(c => {
            const timeDiff = maxwellWeeklyTime - maryWeeklyTime;
            return c.timeMinutes <= timeDiff && dayData.maxwell - c.timeMinutes >= 0;
          });
          
          if (choreToMove) {
            choreToMove.assignedTo = 'Mary';
            dayData.maxwell -= choreToMove.timeMinutes;
            dayData.mary += choreToMove.timeMinutes;
            maxwellWeeklyTime -= choreToMove.timeMinutes;
            maryWeeklyTime += choreToMove.timeMinutes;
            madeChanges = true;
          }
        }
      }
    });
    
    if (!madeChanges) break;
    optimizationRounds++;
  }
  /* eslint-enable no-loop-func */
  
  return assignedChores;
}

function getChoresForWeek(startDate: Date): Chore[] {
  const weekStart = startOfWeek(startDate, { weekStartsOn: 1 });
  const weekNumber = Math.floor((weekStart.getTime() - new Date(2024, 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
  
  return chores.filter(chore => {
    switch (chore.frequency) {
      case 'Weekly':
        return true;
      case '2x Weekly':
        return weekNumber % 2 === 0 || weekNumber % 2 === 1; // Every other week
      case '3x Weekly':
        return weekNumber % 3 === 0 || weekNumber % 3 === 1 || weekNumber % 3 === 2; // Every third week
      case 'Every 2 Weeks':
        return weekNumber % 2 === 0;
      case 'Monthly':
        return weekNumber % 4 === 0; // First week of month
      default:
        return false;
    }
  });
}

function getChoresForDay(day: Date, weeklyChores: Chore[]): Chore[] {
  const dayOfWeek = day.getDay();
  const weekStart = startOfWeek(day, { weekStartsOn: 1 });
  const weekNumber = Math.floor((weekStart.getTime() - new Date(2024, 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
  
  return weeklyChores.filter(chore => {
    switch (chore.frequency) {
      case '2x Weekly':
        // Distribute across the week
        if (weekNumber % 2 === 0) {
          return dayOfWeek === 1 || dayOfWeek === 4; // Monday and Thursday
        } else {
          return dayOfWeek === 2 || dayOfWeek === 5; // Tuesday and Friday
        }
      case '3x Weekly':
        // Distribute across the week
        if (weekNumber % 3 === 0) {
          return dayOfWeek === 1 || dayOfWeek === 3 || dayOfWeek === 5; // Mon, Wed, Fri
        } else if (weekNumber % 3 === 1) {
          return dayOfWeek === 2 || dayOfWeek === 4 || dayOfWeek === 6; // Tue, Thu, Sat
        } else {
          return dayOfWeek === 3 || dayOfWeek === 5 || dayOfWeek === 0; // Wed, Fri, Sun
        }
      case 'Every 2 Weeks':
        return weekNumber % 2 === 0;
      case 'Monthly':
        return weekNumber % 4 === 0;
      default:
        return false;
    }
  });
}

export function getWeeklyStats(assignedChores: AssignedChore[], startDate: Date) {
  const weekStart = startOfWeek(startDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(startDate, { weekStartsOn: 1 });
  
  const weekChores = assignedChores.filter(chore => {
    const choreDate = parseISO(chore.date);
    return choreDate >= weekStart && choreDate <= weekEnd;
  });
  
  const maryChores = weekChores.filter(chore => chore.assignedTo === 'Mary');
  const maxwellChores = weekChores.filter(chore => chore.assignedTo === 'Maxwell');
  
  return {
    mary: {
      totalTime: maryChores.reduce((sum, chore) => sum + chore.timeMinutes, 0),
      completedChores: maryChores.filter(chore => chore.completed).length,
      totalChores: maryChores.length
    },
    maxwell: {
      totalTime: maxwellChores.reduce((sum, chore) => sum + chore.timeMinutes, 0),
      completedChores: maxwellChores.filter(chore => chore.completed).length,
      totalChores: maxwellChores.length
    }
  };
} 